import React, { useEffect, useRef, useState } from 'react';
import {
  Grid,
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  IconButton,
  Button,
  InputAdornment,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  UploadFileOutlined,
  CloseOutlined,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { toast } from 'react-toastify';
import colors from '../../../../constant/colors.js'; // Ensure this path is correct based on your folder structure
import { useSelector, useDispatch } from 'react-redux';
import { getDoctorAppointmentAction } from '../../../../redux/appointment/doctor/doctorAppointmentActions.js';
import { DOCTOR_UPLOAD } from '../../../../services/apiUrl.js';
import * as mainService from '../../../../services/mainService.js';

const useStyles = makeStyles(() => ({
  rootCard: {
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfbff 100%)',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0 6px 18px rgba(23, 43, 77, 0.08)',
  },
  // If inside a dialog, we want a clean background without shadows
  dialogRoot: {
    background: '#ffffff',
    padding: 24, // More padding for the dialog view
    height: '100%',
  },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  fileUploadCont: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    border: '2px dashed #e6e9ef',
    padding: 20, // Increased padding for better click area
    borderRadius: 10,
    textAlign: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    transition: 'all 0.3s ease',
    minHeight: '150px',
    '&:hover': {
      borderColor: colors.green,
      backgroundColor: '#f0f9f4',
    },
  },
  submitButton: {
    marginTop: 12,
    background: colors.green,
    color: '#fff',
    '&:hover': {
      backgroundColor: colors.green, // Or a slightly darker shade if available
      opacity: 0.95,
    },
  },
  pill: { marginRight: 8, marginTop: 8 },
  uploadedFileChip: {
    marginRight: 8,
    marginTop: 8,
    backgroundColor: '#e8f5e9',
    borderColor: '#c8e6c9',
  },
}));

const a11yProps = (index) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`,
});

// Added isDialog prop to adjust styling when used inside the popup
export default function SendMedicationIntegrated({ selectedListItem, onSuccess, isDialog = false }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const prescriptionUploadRef = useRef(null);
  const { userData } = useSelector((s) => ({ userData: s.auth.userData }));

  const [structuredDose, setStructuredDose] = useState([0, 0, 0, 0]);
  const [tabIndex, setTabIndex] = useState(0);
  const [note, setNote] = useState('');

  // Search States
  const [medicineResults, setMedicineResults] = useState([]);
  const [medicineSearchTerm, setMedicineSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [offset, setOffset] = useState(0);

  // Prescription Builder States
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [dosageType, setDosageType] = useState('');
  const [quantity, setQuantity] = useState('');
  const [prescriptionItems, setPrescriptionItems] = useState([]);
  const [prescriptionType, setPrescriptionType] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Upload States
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedPrescriptionUrl, setUploadedPrescriptionUrl] = useState('');
  const [uploadNote, setUploadNote] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const LIMIT = 20;

  const callSearchApi = async (search = '', limit = LIMIT, nextOffset = 0, append = false) => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const payload = {
        search: search,
        limit: limit,
        offset: nextOffset,
        token: userData?.token || ''
      };

      console.log('Calling getMedicineLists with payload:', payload);

      const res = await mainService.getMedicineLists(payload);

      console.log('API Response:', res);

      // Handle the response based on your API structure
      // Adjust these paths based on your actual response structure
      const apiData = res?.response?.data?.data || res?.data?.data || [];
      const pagination = res?.response?.data?.pagination || res?.data?.pagination || {};

      setMedicineResults((prev) => (append ? [...prev, ...apiData] : apiData));
      setHasMore(Boolean(pagination?.hasMore));
      setOffset(Number(pagination?.offset || 0) + Number(pagination?.limit || limit));

    } catch (err) {
      console.error('Medicine search API error:', err);
      toast.error('Failed to fetch medicines. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    callSearchApi('', LIMIT, 0, false);
  }, []);

  const handleSearch = () => {
    setOffset(0);
    callSearchApi(medicineSearchTerm, LIMIT, 0, false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !isLoading) {
      callSearchApi(medicineSearchTerm, LIMIT, offset, true);
    }
  };

  const handlePrescriptionFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, JPEG, and PNG files are allowed');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await mainService.fileUpload(DOCTOR_UPLOAD, formData, userData.token);

      if (response?.response?.data) {
        setUploadedPrescriptionUrl(response.response.data);
        setUploadedFiles((prev) => [...prev, { name: file.name, url: response.response.data }]);
        toast.success('File uploaded successfully');
      } else {
        toast.error('Failed to upload file');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading file');
    } finally {
      setIsUploading(false);
    }
  };

  const submitUploadedPrescription = async () => {
    if (!uploadedPrescriptionUrl) {
      toast.error('Please upload a prescription file first');
      return;
    }

    try {
      const data = {
        appointmentId: selectedListItem?._id,
        prescriptionUrl: uploadedPrescriptionUrl,
        note: uploadNote,
      };

      const res = await mainService.getSendMedicationApi({
        payload: data,
        token: userData.token,
      });

      if (res.response_type === 'fail') {
        toast.error(res?.response?.message, { position: 'bottom-center', closeButton: false, autoClose: 2000 });
      } else {
        toast.success(res?.response?.message, { position: 'bottom-center', closeButton: false, autoClose: 2000 });
        dispatch(getDoctorAppointmentAction({ token: userData.token, status: 4 }));
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit prescription');
    }
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    if (uploadedFiles.length === 1) {
      setUploadedPrescriptionUrl('');
    }
  };

  const handleMedicineSelect = (med) => {
    setSelectedMedicine(med);
    setEditingIndex(null);
  };

  const handleEditPrescription = (item, index) => {
    setSelectedMedicine(item);
    setDosageType(item.dosageType);
    setQuantity(item.quantity);
    if (item.structuredDose) {
      setStructuredDose(item.structuredDose);
    }
    setEditingIndex(index);
    // Switch to Free Text if specific dose type isn't standard
    if (!['OD', 'BD', 'TDS', 'SOS'].includes(item.dosageType) && !item.structuredDose) {
      setPrescriptionType('freeText');
    } else {
      setPrescriptionType(item.prescriptionType || 'standard');
    }
  };

  const handleAddToPrescription = () => {
    if (!selectedMedicine) return toast.error('Select a medicine first');
    if (!prescriptionType) return toast.error('Select prescription type first');

    if ((prescriptionType === 'standard' || prescriptionType === 'structured') && !quantity) {
      return toast.error('Enter quantity');
    }

    const item = {
      ...selectedMedicine,
      dosageType: prescriptionType === 'structured' ? structuredDose.join('-') : dosageType,
      quantity,
      structuredDose: prescriptionType === 'structured' ? [...structuredDose] : null,
      prescriptionType,
    };

    if (editingIndex !== null) {
      setPrescriptionItems((p) => {
        const updated = [...p];
        updated[editingIndex] = item;
        return updated;
      });
      setEditingIndex(null);
      toast.success('Prescription updated');
    } else {
      setPrescriptionItems((p) => [...p, item]);
      toast.success('Medicine added to prescription');
    }

    // Reset Form
    setSelectedMedicine(null);
    setQuantity('');
    setDosageType('OD');
    setStructuredDose([0, 0, 0, 0]);
  };

  const removePrescriptionItem = (index) => {
    setPrescriptionItems((p) => p.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setSelectedMedicine(null);
    }
  };

  const submitPrescription = async () => {
    try {
      if (prescriptionItems.length === 0) {
        toast.error('Please add at least one medicine to the prescription');
        return;
      }

      const payload = {
        appointmentId: selectedListItem?._id,
        note,
        jsonData: prescriptionItems.map((m) => ({
          id: m.pzn,
          name: m.name,
          dosage: m.dosageType,
          qty: m.quantity,
          companyName: m.companyName,
          structuredDose: m.structuredDose,
          prescriptionType: m.prescriptionType,
        })),
      };

      const res = await mainService.getSendMedicationApi({
        payload: payload,
        token: userData.token,
      });

      if (res.response_type === 'fail') {
        toast.error(res?.response?.message || 'Failed to submit prescription', {
          position: 'bottom-center',
          closeButton: false,
          autoClose: 2000,
        });
      } else {
        toast.success(res?.response?.message || 'Prescription submitted successfully', {
          position: 'bottom-center',
          closeButton: false,
          autoClose: 2000,
        });
        dispatch(getDoctorAppointmentAction({ token: userData.token, status: 4 }));
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error('Prescription submission error:', err);
      toast.error('Failed to submit prescription. Please try again.');
    }
  };

  // --- PDF Preview Component (Internal) ---
  const PdfPreviewDialog = () => {
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });

    return (
      <Dialog open={showPdfPreview} onClose={() => setShowPdfPreview(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Prescription Preview</Typography>
            <IconButton onClick={() => setShowPdfPreview(false)}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ padding: 3 }}>
            <Box sx={{ borderBottom: '3px solid #1976d2', pb: 2, mb: 2 }}>
              <Typography variant="h5" fontWeight="bold" color="primary">Medical Center Hospital</Typography>
              <Typography variant="body2" color="textSecondary">123 Healthcare Avenue, Medical District</Typography>
            </Box>
            {/* ... PDF Preview Content ... */}
            <Typography variant="subtitle1" gutterBottom>Prescription Items:</Typography>
            {prescriptionItems.map((item, idx) => (
              <Typography key={idx} variant="body2">
                {idx + 1}. {item.name} - {item.dosageType} ({item.quantity})
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPdfPreview(false)}>Close</Button>
          <Button variant="contained" onClick={() => window.print()}>Print/Download PDF</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // --- Main Render ---
  return (
    <Grid container spacing={0}>
      <Grid item xs={12}>
        {/* If isDialog is true, we remove the shadow/card look to blend with parent Modal */}
        <Box className={isDialog ? classes.dialogRoot : classes.rootCard}>

          {/* Header Title Removed here because Parent Dialog handles it */}

          <Tabs
            value={tabIndex}
            onChange={(e, v) => setTabIndex(v)}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab label="Upload Prescription" {...a11yProps(0)} sx={{ fontWeight: '600' }} />
            <Tab label="Create Prescription" {...a11yProps(1)} sx={{ fontWeight: '600' }} />
          </Tabs>

          {/* === TAB 0: UPLOAD PRESCRIPTION === */}
          {tabIndex === 0 && (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
                Upload a pre-made prescription document (PDF, JPG, JPEG, PNG). Max file size: 10MB
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    className={classes.fileUploadCont}
                    onClick={() => prescriptionUploadRef.current?.click()}
                  >
                    <UploadFileOutlined sx={{ fontSize: 48, color: colors.green, mb: 1 }} />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {isUploading ? 'Uploading...' : 'Click to upload prescription'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        PDF, JPG, JPEG & PNG files (Max 10MB)
                      </Typography>
                    </Box>
                  </Paper>
                  <input
                    ref={prescriptionUploadRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    onChange={handlePrescriptionFileUpload}
                    disabled={isUploading}
                  />

                  {uploadedFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Uploaded:</Typography>
                      {uploadedFiles.map((f, idx) => (
                        <Chip
                          key={idx}
                          label={f.name}
                          onDelete={() => removeUploadedFile(idx)}
                          className={classes.uploadedFileChip}
                          icon={<PdfIcon />}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Additional Notes"
                    fullWidth
                    multiline
                    rows={6}
                    placeholder="Add any additional notes for the patient..."
                    value={uploadNote}
                    onChange={(e) => setUploadNote(e.target.value)}
                    variant="outlined"
                    sx={{ backgroundColor: '#fafafa' }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      className={classes.submitButton}
                      onClick={submitUploadedPrescription}
                      disabled={!uploadedPrescriptionUrl || isUploading}
                      size="large"
                      sx={{ minWidth: 200 }}
                    >
                      {isUploading ? 'Uploading...' : 'Submit Prescription'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* === TAB 1: CREATE PRESCRIPTION === */}
          {tabIndex === 1 && (
            <Box>
              <Grid container spacing={3}>
                {/* Left Side: Search & List */}
                <Grid item xs={12} md={5}>
                  <Paper sx={{ p: 0, overflow: 'hidden', border: '1px solid #eee' }} elevation={0}>
                    <Box sx={{ p: 2, backgroundColor: '#f5f5f5', display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search medicines..."
                        value={medicineSearchTerm}
                        onChange={(e) => setMedicineSearchTerm(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="action" />
                            </InputAdornment>
                          ),
                          sx: { backgroundColor: '#fff' }
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={isLoading}
                        startIcon={<SearchIcon />}
                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        Search
                      </Button>
                    </Box>
                    <List
                      sx={{ maxHeight: 400, overflowY: 'auto', p: 0 }}
                      onScroll={handleScroll}
                    >
                      {medicineResults.length ? (
                        medicineResults.map((m) => (
                          <ListItem
                            key={m.pzn}
                            divider
                            button
                            onClick={() => handleMedicineSelect(m)}
                            selected={selectedMedicine?.pzn === m.pzn}
                            sx={{ '&.Mui-selected': { backgroundColor: '#e3f2fd' } }}
                          >
                            <ListItemText
                              primary={m.name}
                              secondary={`${m.companyName} • ${m.formDescription || ''}`}
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText
                            primary={isLoading ? 'Loading...' : 'No medicines found'}
                            secondary="Try searching for a different name"
                            sx={{ textAlign: 'center', py: 4 }}
                          />
                        </ListItem>
                      )}
                      {isLoading && medicineResults.length > 0 && (
                        <ListItem>
                          <ListItemText primary="Loading more..." sx={{ textAlign: 'center' }} />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </Grid>

                {/* Right Side: Details & Config */}
                <Grid item xs={12} md={7}>
                  <Paper sx={{ p: 3, border: '1px solid #eee', height: '100%' }} elevation={0}>
                    {selectedMedicine ? (
                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="h6" color="primary">{selectedMedicine.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {selectedMedicine.companyName}
                            </Typography>
                          </Box>
                          {editingIndex !== null && (
                            <Chip label="Editing Mode" color="warning" size="small" />
                          )}
                        </Box>

                        <FormControl fullWidth sx={{ mt: 3 }} size="small">
                          <InputLabel>Prescription Type</InputLabel>
                          <Select
                            value={prescriptionType}
                            label="Prescription Type"
                            onChange={(e) => setPrescriptionType(e.target.value)}
                          >
                            <MenuItem value="standard">Standard (OD, BD, etc.)</MenuItem>
                            <MenuItem value="structured">Structured (Morning-Noon-Evening-Night)</MenuItem>
                            <MenuItem value="freeText">Free Text (Custom instructions)</MenuItem>
                          </Select>
                        </FormControl>

                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                          {prescriptionType === 'freeText' ? (
                            <TextField
                              label="Dosage Instructions"
                              fullWidth
                              multiline
                              rows={2}
                              placeholder="e.g. 1 tablet morning and night after food"
                              value={dosageType}
                              onChange={(e) => setDosageType(e.target.value)}
                              size="small"
                            />
                          ) : (
                            <>
                              {prescriptionType === 'structured' ? (
                                <Box>
                                  <Typography variant="caption" color="textSecondary" gutterBottom>
                                    Dosage (Morning - Noon - Evening - Night)
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    {['M', 'N', 'E', 'N'].map((label, idx) => (
                                      <TextField
                                        key={idx}
                                        type="number"
                                        label={label}
                                        value={structuredDose[idx]}
                                        onChange={(e) => {
                                          const newDose = [...structuredDose];
                                          newDose[idx] = e.target.value === '' ? 0 : Number(e.target.value);
                                          setStructuredDose(newDose);
                                        }}
                                        inputProps={{ min: 0, step: 0.5, style: { textAlign: 'center' } }}
                                        variant="outlined"
                                        size="small"
                                        sx={{ flex: 1 }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              ) : prescriptionType === 'standard' ? (
                                <FormControl fullWidth size="small">
                                  <InputLabel>Dosage Frequency</InputLabel>
                                  <Select
                                    value={dosageType}
                                    label="Dosage Frequency"
                                    onChange={(e) => setDosageType(e.target.value)}
                                  >
                                    <MenuItem value="OD">OD (Once daily)</MenuItem>
                                    <MenuItem value="BD">BD (Twice daily)</MenuItem>
                                    <MenuItem value="TDS">TDS (Thrice daily)</MenuItem>
                                    <MenuItem value="SOS">SOS (As needed)</MenuItem>
                                    <MenuItem value="QID">QID (Four times a day)</MenuItem>
                                  </Select>
                                </FormControl>
                              ) : null}
                            </>
                          )}

                          <TextField
                            label="Quantity / Duration"
                            fullWidth
                            size="small"
                            placeholder="e.g., 30 tablets or 10 days"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            sx={{ mt: 2 }}
                          />
                        </Box>

                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={editingIndex !== null ? <EditIcon /> : <AddIcon />}
                          onClick={handleAddToPrescription}
                          sx={{ mt: 2, backgroundColor: colors.green }}
                        >
                          {editingIndex !== null ? 'Update Item' : 'Add to List'}
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 8, opacity: 0.6 }}>
                        <AddIcon sx={{ fontSize: 40, color: '#ccc' }} />
                        <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                          Select a medicine from the list to begin
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {/* Prescription List Summary */}
              {prescriptionItems.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom>Prescription Summary</Typography>
                  <Paper variant="outlined">
                    {prescriptionItems.map((it, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: idx !== prescriptionItems.length - 1 ? '1px solid #eee' : 'none',
                          backgroundColor: editingIndex === idx ? '#f0f7ff' : '#fff'
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">{it.name}</Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            Type: {it.prescriptionType} | Qty: {it.quantity}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            Dosage: {it.dosageType}
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton size="small" onClick={() => handleEditPrescription(it, idx)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => removePrescriptionItem(idx)} color="error">
                            <CloseOutlined fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Paper>

                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="Final Note for Patient"
                      fullWidth
                      multiline
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Any dietary advice or special instructions..."
                    />
                  </Box>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={() => setShowPdfPreview(true)}>
                      Preview PDF
                    </Button>
                    <Button
                      variant="contained"
                      onClick={submitPrescription}
                      className={classes.submitButton}
                      size="large"
                      sx={{ minWidth: 200 }}
                    >
                      Submit Final Prescription
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Grid>

      <PdfPreviewDialog />
    </Grid>
  );
}